import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { buildUrl, isDefined } from '@trimble-gcs/common';
import {
  catchError,
  combineLatestWith,
  EMPTY,
  filter,
  iif,
  map,
  Observable,
  of,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { ConnectProject } from 'trimble-connect-workspace-api';
import { AppState } from '../app-state/app.state';
import { AuthState } from '../auth/auth.state';
import { ClientHeaderService } from '../client-header/client-header.service';
import { isApiErrorType } from '../error-handling/api-error-type';
import { GainsightService } from '../gainsight/gainsight.service';
import { injectLogger } from '../logging/logger-injection';
import { isRegionNotFoundError } from '../tenancy/region-not-found.error';
import { GlobalRegion } from '../tenancy/tenancy.models';
import { TenancyService } from '../tenancy/tenancy.service';
import { SetLicenses } from './license.actions';
import { License, LicenseStatus, LicenseType } from './license.models';
import { LicenseState } from './license.state';

export class LicenseService {
  protected readonly store = inject(Store);

  private readonly http = inject(HttpClient);
  private readonly tenancyService = inject(TenancyService);
  private readonly clientHeaderService = inject(ClientHeaderService);
  private readonly gainsightService = inject(GainsightService);
  private readonly logger = injectLogger('LicenseService');

  loadLicenses() {
    return this.fetchAndCacheLicenses();
  }

  hasActiveLicense() {
    return this.getLicenses().pipe(
      switchMap(() => this.store.selectOnce(LicenseState.hasActiveLicense)),
      catchError((err) => {
        this.logger.error(`Load license error`, {}, err);
        return of(false);
      }),
    );
  }

  private getLicenses() {
    return this.store
      .selectOnce(LicenseState.licenses)
      .pipe(
        switchMap((licenses) =>
          isDefined(licenses) ? of(licenses) : this.fetchAndCacheLicenses(),
        ),
      );
  }

  protected fetchAndCacheLicenses(): Observable<License[]> {
    /**
     * There is a very subtle nuance here.
     * The `pipe` function has overloads with up to 9 generic type parameters,
     * after which it returns an `Observable<unknown>`. For that reason, the variables are split,
     * so that a new `pipe` can be introduced after 9 operators, to preserve the type.
     */
    const licenses$ = this.store.select(AppState.project).pipe(
      filter(isDefined),
      switchMap((connectProject) =>
        this.tenancyService.getGlobalRegion(connectProject).pipe(
          map((globalRegion) => {
            return [connectProject, globalRegion] as [ConnectProject, GlobalRegion];
          }),
        ),
      ),
      map(([connectProject, globalRegion]) =>
        buildUrl(
          globalRegion.sdsBaseUrl,
          `/licenses?projectId=${connectProject.id}&projectLocation=${globalRegion.connectRegion}`,
        ),
      ),
      combineLatestWith(this.store.select(AuthState.accessToken)),
      filter(([, token]) => {
        return isDefined(token);
      }),
      take(1),
      switchMap(([url, token]) => {
        return this.http.get<License[] | null>(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...this.clientHeaderService.getClientHeaders(),
          },
        });
      }),
      map((licenses: License[] | null) => licenses ?? []),
      switchMap((licenses: License[]) =>
        this.store.dispatch(new SetLicenses(licenses)).pipe(map(() => licenses)),
      ),
    );

    return licenses$.pipe(
      catchError((err: unknown) => {
        if (isRegionNotFoundError(err)) {
          return this.gainsightService
            .regionNotFound(err.connectRegion)
            .pipe(switchMap(() => throwError(() => err)));
        }

        const httpError = err as HttpErrorResponse;
        return iif(
          () => isApiErrorType(httpError, 'ACCOUNT_NOT_ALLOWED'),
          this.gainsightService.accountNotAllowed(),
          EMPTY,
        ).pipe(switchMap(() => throwError(() => httpError)));
      }),
    );
  }
}

export class LocalhostLicenseService extends LicenseService {
  override fetchAndCacheLicenses(): Observable<License[]> {
    const licenses: License[] = [
      {
        skuName: LicenseType.MaxStorage,
        type: LicenseType.MaxStorage,
        startDate: new Date(),
        quota: 10 * 1024 * 1024,
        status: LicenseStatus.Active,
      },
    ];

    return this.store.dispatch(new SetLicenses(licenses)).pipe(map(() => licenses));
  }
}
